/**
 * useResizablePanel — shared hook for right-side resizable slide-in panels.
 * data-fit LLC · DecisionFlow · S946
 *
 * Mirrors the left nav resize pattern (CompAdminShell NAV_MIN/MAX/DEFAULT +
 * handleDragMouseDown) but for right-anchored panels whose drag handle sits
 * on the LEFT edge of the panel. Dragging left = wider; dragging right = narrower.
 *
 * Width is persisted in sessionStorage under `storageKey` so each panel
 * remembers its own width independently across route changes and panel
 * open/close cycles. Different panels use different keys — they never share
 * a persisted width.
 *
 * Usage:
 *   const { panelWidth, dragHandleProps } = useResizablePanel({
 *     storageKey: 'df_panel_payout',   // unique per panel type
 *     defaultWidth: 680,
 *     minWidth: 320,
 *     maxWidth: 1100,
 *   });
 *
 *   // In JSX — add drag handle as FIRST child of the panel div,
 *   // and replace the hardcoded Tailwind width class with a style prop:
 *   //
 *   //   div  style={{ width: panelWidth }}  className="fixed top-0 right-0 ..."
 *   //     div  {...dragHandleProps}  -- drag handle, left edge
 *   //     ...panel content...
 *   //   /div
 *
 * dragHandleProps provides:
 *   onMouseDown  — starts the drag
 *   style        — positions the handle on the panel's left edge
 *   title        — "Drag to resize"
 *   className    — cursor + hover highlight (matches left nav handle)
 */

import { useState, useCallback } from 'react';

export function useResizablePanel({
  storageKey,
  defaultWidth = 480,
  minWidth     = 280,
  maxWidth     = 1100,
}) {
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      return !isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth
        ? parsed
        : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });

  const handleDragMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      const startX     = e.clientX;
      const startWidth = panelWidth;

      function onMouseMove(e) {
        // Right panel: dragging LEFT increases width (panel grows leftward)
        const delta = startX - e.clientX;
        const next  = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));
        setPanelWidth(next);
        try { sessionStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
      }

      function onMouseUp() {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup',   onMouseUp);
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup',   onMouseUp);
    },
    [panelWidth, minWidth, maxWidth, storageKey]
  );

  const dragHandleProps = {
    onMouseDown: handleDragMouseDown,
    title: 'Drag to resize',
    style: {
      position:  'absolute',
      top:       0,
      left:      0,
      width:     4,
      height:    '100%',
      cursor:    'col-resize',
      zIndex:    10,
    },
    className:
      'hover:bg-[rgb(var(--color-primary))]/30 active:bg-[rgb(var(--color-primary))]/50 transition-colors',
  };

  return { panelWidth, dragHandleProps };
}
